import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { BRAND, Logo, formatCurrency } from '@careandkey/shared';
import { supabase } from '../lib/supabase';

type Inv = {
  invoice_number: string | null; kind: string; status: string; currency: string; subtotal: number; tax_total: number; tax_label: string | null; total: number; amount_paid: number; balance: number;
  issue_date: string | null; due_date: string | null;
  clients: { name: string } | null; properties: { name: string } | null;
  invoice_lines: { description: string; quantity: number; unit_price: number; line_total: number; position: number }[];
};

export function InvoicePrint() {
  const { id } = useParams();
  const { t, i18n } = useTranslation();
  const [inv, setInv] = useState<Inv | null>(null);

  useEffect(() => {
    supabase.from('invoices').select('invoice_number,kind,status,currency,subtotal,tax_total,tax_label,total,amount_paid,balance,issue_date,due_date,clients(name),properties(name),invoice_lines(description,quantity,unit_price,line_total,position)').eq('id', id).single().then((r) => setInv(r.data as unknown as Inv));
  }, [id]);
  useEffect(() => { if (inv) { const x = setTimeout(() => window.print(), 400); return () => clearTimeout(x); } }, [inv]);

  const C = BRAND.colors;
  if (!inv) return <div className="p-10 text-muted">…</div>;
  const lines = [...(inv.invoice_lines ?? [])].sort((a, b) => a.position - b.position);
  const title = inv.kind === 'credit_note' ? t('inv.creditNote') : t('nav.invoices');
  return (
    <div className="mx-auto max-w-[800px] bg-white p-10 text-[#0B0F17]" style={{ fontFamily: 'Geist, system-ui, sans-serif' }}>
      <div className="flex items-start justify-between border-b-2 pb-5" style={{ borderColor: C.ink }}>
        <Logo variant="light" size={34} />
        <div className="text-right"><div className="text-[20px] font-semibold">{title}</div><div className="font-mono text-sm text-[#667085]">{inv.invoice_number ?? '—'}</div></div>
      </div>
      <div className="mt-6 grid grid-cols-2 gap-4 text-sm">
        <div><div className="text-xs text-[#667085]">{t('nav.clients')}</div><div className="font-medium">{inv.clients?.name ?? '—'}</div></div>
        <div><div className="text-xs text-[#667085]">{t('nav.properties')}</div><div className="font-medium">{inv.properties?.name ?? '—'}</div></div>
        <div><div className="text-xs text-[#667085]">{t('inv.issueDate')}</div><div className="font-mono">{inv.issue_date ?? '—'}</div></div>
        <div><div className="text-xs text-[#667085]">{t('inv.dueDate')}</div><div className="font-mono">{inv.due_date ?? '—'}</div></div>
      </div>
      <table className="mt-6 w-full border-collapse text-sm">
        <thead><tr style={{ background: '#F7F8FA' }} className="text-left text-xs text-[#667085]"><th className="p-2.5">{t('cost.description')}</th><th className="p-2.5 text-right">{t('cost.quantity')}</th><th className="p-2.5 text-right">{t('cat.unitPrice')}</th><th className="p-2.5 text-right">{t('cost.total')}</th></tr></thead>
        <tbody>
          {lines.map((l, i) => (
            <tr key={i} className="border-b border-[#EAECF0]">
              <td className="p-2.5">{l.description}</td>
              <td className="p-2.5 text-right font-mono">{l.quantity}</td>
              <td className="p-2.5 text-right font-mono">{formatCurrency(l.unit_price, inv.currency, i18n.language)}</td>
              <td className="p-2.5 text-right font-mono">{formatCurrency(l.line_total, inv.currency, i18n.language)}</td>
            </tr>
          ))}
        </tbody>
      </table>
      <div className="mt-4 flex flex-col items-end gap-1 text-sm">
        <span className="text-[#667085]">{t('cost.total')} HT: <span className="font-mono text-[#0B0F17]">{formatCurrency(inv.subtotal, inv.currency, i18n.language)}</span></span>
        {inv.tax_total !== 0 && <span className="text-[#667085]">{inv.tax_label ?? t('inv.tax')}: <span className="font-mono text-[#0B0F17]">{formatCurrency(inv.tax_total, inv.currency, i18n.language)}</span></span>}
        <span className="text-lg font-semibold" style={{ color: C.accent }}>{formatCurrency(inv.total, inv.currency, i18n.language)}</span>
        {inv.amount_paid > 0 && <span className="text-[#667085]">{t('inv.balance')}: <span className="font-mono">{formatCurrency(inv.balance, inv.currency, i18n.language)}</span></span>}
      </div>
      <p className="mt-10 text-center text-xs text-[#98A2B3]">{BRAND.name} · {BRAND.domain}</p>
    </div>
  );
}
